import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { auditService, AuditTemplate } from '@/firebase/auditService';
import ComplianceForm from './ComplianceForm';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { normalizeSubscriptionPlan } from '@/firebase/subscriptionService';
import { aiSearchService, AISuggestion, NewTemplateSuggestion, NewAuditTemplateSuggestion } from '@/services/aiSearchService';
import { loadDefaultTemplatesForOrganization } from '@/services/industryTemplateService';
import { Sparkles, Plus, AlertCircle, MapPin, FileCheck, Trash2, Edit, Play, History, Info } from 'lucide-react';
import AssignLocationsToTemplatePanel from './AssignLocationsToTemplatePanel';
import AuditHistoryModal from './AuditHistoryModal';
import CreateAuditInstanceModal from './CreateAuditInstanceModal';

const ComplianceManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  // Tab state - default to 'compliances' or from URL
  const activeTab = searchParams.get('tab') || 'compliances';
  const setActiveTab = (tab: 'compliances' | 'audits') => {
    setSearchParams({ tab });
  };
  
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<ComplianceTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All Categories');
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestion | null>(null);
  const [newTemplateSuggestions, setNewTemplateSuggestions] = useState<NewTemplateSuggestion[] | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [templateToAdd, setTemplateToAdd] = useState<Partial<ComplianceTemplate> | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalMessage, setUpgradeModalMessage] = useState<string>('');
  const [templateForLocationAssignment, setTemplateForLocationAssignment] = useState<ComplianceTemplate | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Audit template state
  const [auditTemplates, setAuditTemplates] = useState<AuditTemplate[]>([]);
  const [isLoadingAudits, setIsLoadingAudits] = useState(false);
  const [auditSearchTerm, setAuditSearchTerm] = useState('');
  const [auditAiSuggestions, setAuditAiSuggestions] = useState<NewAuditTemplateSuggestion[] | null>(null);
  const [isLoadingAuditAI, setIsLoadingAuditAI] = useState(false);
  const auditSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showAuditReviewPanel, setShowAuditReviewPanel] = useState(false);
  const [auditTemplateToReview, setAuditTemplateToReview] = useState<NewAuditTemplateSuggestion | null>(null);
  const [editableAuditTemplate, setEditableAuditTemplate] = useState<Partial<NewAuditTemplateSuggestion> | null>(null);
  const [editingAuditTemplate, setEditingAuditTemplate] = useState<AuditTemplate | null>(null);
  const [complianceTemplateSearch, setComplianceTemplateSearch] = useState('');
  const [isLoadingAuditDetails, setIsLoadingAuditDetails] = useState(false);
  const [showRunAuditModal, setShowRunAuditModal] = useState(false);
  const [selectedAuditTemplateForRun, setSelectedAuditTemplateForRun] = useState<AuditTemplate | null>(null);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [selectedAuditTemplateForHistory, setSelectedAuditTemplateForHistory] = useState<AuditTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [currentOrganization?.id]);

  // Load audit templates when on audits tab
  useEffect(() => {
    if (activeTab === 'audits' && currentOrganization?.id) {
      loadAuditTemplates();
    }
  }, [activeTab, currentOrganization?.id]);

  // Debounced AI search for audit templates when no results are found
  useEffect(() => {
    // Only run for audits tab
    if (activeTab !== 'audits') {
      setAuditAiSuggestions(null);
      return;
    }

    // Clear previous timeout
    if (auditSearchTimeoutRef.current) {
      clearTimeout(auditSearchTimeoutRef.current);
    }

    // Clear AI suggestion when search term is cleared
    if (!auditSearchTerm) {
      setAuditAiSuggestions(null);
      return;
    }

    // Calculate filtered audit templates
    const currentFiltered = auditTemplates.filter(t => {
      const matchesSearch = !auditSearchTerm || 
        t.name.toLowerCase().includes(auditSearchTerm.toLowerCase()) ||
        t.auditType.toLowerCase().includes(auditSearchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(auditSearchTerm.toLowerCase());
      return matchesSearch;
    });

    // Only trigger AI search if there are no filtered templates and search term is long enough
    const hasResults = currentFiltered.length > 0;
    
    if (!hasResults && auditSearchTerm.length >= 3) {
      // Debounce AI search by 800ms
      auditSearchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingAuditAI(true);
        setAuditAiSuggestions(null);
        
        try {
          const existingAuditTemplateNames = auditTemplates.map(t => t.name);
          
          // Generate audit template suggestions
          const suggestions = await aiSearchService.generateAuditTemplateSuggestions(
            auditSearchTerm,
            existingAuditTemplateNames,
            templates // Pass compliance templates for context
          );
          
          console.log('AI audit search results:', { 
            auditSearchTerm, 
            suggestions: suggestions?.length || 0
          });
          
          if (suggestions && suggestions.length > 0) {
            setAuditAiSuggestions(suggestions);
          }
        } catch (error) {
          console.error('Error fetching AI audit suggestions:', error);
          setAuditAiSuggestions(null);
        } finally {
          setIsLoadingAuditAI(false);
        }
      }, 800);
    } else {
      setAuditAiSuggestions(null);
    }

    return () => {
      if (auditSearchTimeoutRef.current) {
        clearTimeout(auditSearchTimeoutRef.current);
      }
    };
  }, [auditSearchTerm, auditTemplates, activeTab, templates]);

  // Debounced AI search when no results are found
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear AI suggestion when search term is cleared
    if (!searchTerm) {
      setAiSuggestion(null);
      return;
    }

    // Calculate filtered templates inline to check if we have results
    const currentFiltered = templates.filter(t => {
      const matchesCategory = selectedCategory === 'All Categories' || t.category === selectedCategory;
      const matchesSearch = !searchTerm || 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.owner_role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.jurisdiction.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });

    // Only trigger AI search if there are no filtered templates and search term is long enough
    const hasResults = currentFiltered.length > 0;
    
    if (!hasResults && searchTerm.length >= 3) {
      // Debounce AI search by 800ms
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingAI(true);
        setAiSuggestion(null);
        setNewTemplateSuggestions(null);
        
        try {
          // First, search default templates for matches
          const existingTemplateNames = templates.map(t => t.name);
          const defaultTemplateMatches = aiSearchService.searchDefaultTemplates(searchTerm, existingTemplateNames);
          
          console.log('Default template search results:', { 
            searchTerm, 
            defaultMatches: defaultTemplateMatches.length
          });
          
          if (defaultTemplateMatches.length > 0) {
            // Show default template matches first - no need to call AI
            setNewTemplateSuggestions(defaultTemplateMatches);
            setAiSuggestion(null);
          } else {
            // No default matches, try AI generation
            const categories = Array.from(new Set(templates.map(t => t.category)));
            
            // Generate NEW template suggestions from external knowledge
            const newSuggestions = await aiSearchService.generateNewTemplateSuggestions(searchTerm, categories, existingTemplateNames);
            
            console.log('AI search results:', { 
              searchTerm, 
              newSuggestions: newSuggestions?.length || 0
            });
            
            if (newSuggestions && newSuggestions.length > 0) {
              // We have new suggestions - prioritize these and hide existing templates
              setNewTemplateSuggestions(newSuggestions);
              setAiSuggestion(null);
            } else {
              // No new suggestions generated - could be API issue or no matches found
              setNewTemplateSuggestions(null);
              setAiSuggestion(null);
              
              // Only show existing if exact name match
              const exactNameMatch = templates.find(t => 
                t.name.toLowerCase().trim() === searchTerm.toLowerCase().trim()
              );
              
              if (exactNameMatch) {
                setAiSuggestion({
                  reason: `Found existing template: "${exactNameMatch.name}"`,
                  suggestedTemplates: [{
                    name: exactNameMatch.name,
                    category: exactNameMatch.category,
                    why: 'Exact name match'
                  }],
                  alternativeTerms: []
                });
              }
              // If no exact match and no new suggestions, show nothing (user is searching for new items)
            }
          }
        } catch (error) {
          console.error('Error fetching AI suggestions:', error);
          // On error, suppress existing templates - don't show misleading matches
          setNewTemplateSuggestions(null);
          setAiSuggestion(null);
        } finally {
          setIsLoadingAI(false);
        }
      }, 800);
    } else {
      setAiSuggestion(null);
      setNewTemplateSuggestions(null);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedCategory, templates]);

  const loadTemplates = async () => {
    // Don't load templates if no organization
    if (!currentOrganization?.id) {
      setTemplates([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const result = await complianceService.getAllTemplates(currentOrganization.id);
    if (result.success && result.data) {
      setTemplates(result.data);
    }
    setIsLoading(false);
  };

  const loadAuditTemplates = async () => {
    // Don't load templates if no organization
    if (!currentOrganization?.id) {
      setAuditTemplates([]);
      setIsLoadingAudits(false);
      return;
    }

    setIsLoadingAudits(true);
    const result = await auditService.getAllTemplates(currentOrganization.id);
    if (result.success && result.data) {
      setAuditTemplates(result.data);
    } else {
      showNotification(`Error loading audit templates: ${result.error}`, 'error');
    }
    setIsLoadingAudits(false);
  };

  const handleDeleteAuditTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this audit template?')) return;
    
    const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
    const result = await auditService.deleteTemplate(templateId, userInfo);
    if (result.success) {
      await loadAuditTemplates();
      showNotification('Audit template deleted successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleAddAuditFromSuggestion = (suggestion: NewAuditTemplateSuggestion) => {
    if (!currentOrganization?.id) {
      setUpgradeModalMessage('You must have an organization to add audit templates. Please create an organization first.');
      setShowUpgradeModal(true);
      return;
    }

    // Open review panel instead of directly creating
    setAuditTemplateToReview(suggestion);
    setEditableAuditTemplate({ ...suggestion });
    setShowAuditReviewPanel(true);
  };

  const handleCreateAuditTemplate = async () => {
    if (!currentOrganization?.id || !editableAuditTemplate) {
      return;
    }

    // If editing, update instead of create
    if (editingAuditTemplate?.id) {
      await handleUpdateAuditTemplate();
      return;
    }

    try {
      // Find compliance template IDs by matching names
      const complianceTemplateIds: string[] = [];
      if (editableAuditTemplate.complianceTemplateNames && editableAuditTemplate.complianceTemplateNames.length > 0) {
        editableAuditTemplate.complianceTemplateNames.forEach(name => {
          const matchingTemplate = templates.find(t => 
            t.name.toLowerCase().trim() === name.toLowerCase().trim()
          );
          if (matchingTemplate?.id) {
            complianceTemplateIds.push(matchingTemplate.id);
          }
        });
      }

      // Create missing compliance templates first
      const createdComplianceTemplateIds: string[] = [];
      if (editableAuditTemplate.missingComplianceTemplates && editableAuditTemplate.missingComplianceTemplates.length > 0) {
        const { calculateRiskWeight } = await import('@/firebase/complianceService');
        const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;

        for (const missingTemplate of editableAuditTemplate.missingComplianceTemplates) {
          const complianceTemplateData: Omit<ComplianceTemplate, 'id'> = {
            name: missingTemplate.name,
            category: missingTemplate.category,
            frequency: missingTemplate.frequency,
            owner_role: missingTemplate.owner_role,
            evidence_types: missingTemplate.evidence_types,
            pass_fail_criteria: missingTemplate.pass_fail_criteria,
            jurisdiction: missingTemplate.jurisdiction,
            jurisdiction_notes: missingTemplate.jurisdiction_notes,
            risk_level: missingTemplate.risk_level,
            risk_weight: calculateRiskWeight(missingTemplate.risk_level),
            lead_times_days: missingTemplate.lead_times_days,
            organizationId: currentOrganization.id
          };

          const result = await complianceService.addTemplate(
            complianceTemplateData,
            currentOrganization.id,
            userInfo
          );

          if (result.success && result.id) {
            createdComplianceTemplateIds.push(result.id);
          }
        }

        // Reload compliance templates to include newly created ones
        await loadTemplates();
      }

      // Combine existing and newly created compliance template IDs
      const allComplianceTemplateIds = [...complianceTemplateIds, ...createdComplianceTemplateIds];

      // Create audit template
      // If Custom audit type, store custom name in metadata
      const auditType = editableAuditTemplate.auditType || 'Custom';
      const metadata: any = {
        ...editableAuditTemplate.metadata,
        aiGenerated: true,
        sourceQuery: auditSearchTerm
      };
      
      // If custom audit type with custom name, store it in metadata
      if (auditType === 'Custom' && editableAuditTemplate.metadata?.customAuditType) {
        metadata.customAuditTypeName = editableAuditTemplate.metadata.customAuditType;
      }

      const auditTemplateData: Omit<AuditTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
        name: editableAuditTemplate.name || 'Untitled Audit',
        description: editableAuditTemplate.description,
        auditType: auditType,
        frequency: editableAuditTemplate.frequency || 'yearly',
        complianceTemplateIds: allComplianceTemplateIds,
        organizationId: currentOrganization.id,
        isActive: true,
        metadata: metadata
      };

      const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
      const result = await auditService.createTemplate(auditTemplateData, userInfo);

      if (result.success) {
        await loadAuditTemplates();
        setAuditSearchTerm(''); // Clear search to show the new template
        setShowAuditReviewPanel(false);
        setAuditTemplateToReview(null);
        setEditableAuditTemplate(null);
        const missingCount = editableAuditTemplate.missingComplianceTemplates?.length || 0;
        const message = missingCount > 0
          ? `Audit template created successfully with ${missingCount} new compliance template${missingCount !== 1 ? 's' : ''}`
          : 'Audit template created successfully';
        showNotification(message, 'success');
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error creating audit template from suggestion:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  const handleEditAuditTemplate = async (template: AuditTemplate) => {
    if (!template.id) return;

    // Open panel immediately with available data from the table row
    const initialEditable: Partial<NewAuditTemplateSuggestion> = {
      name: template.name,
      description: template.description,
      auditType: template.auditType,
      frequency: template.frequency,
      complianceTemplateNames: [], // Will be loaded
      missingComplianceTemplates: [],
      why_relevant: '',
      metadata: {
        ...template.metadata,
        customAuditType: template.metadata?.customAuditTypeName || ''
      }
    };

    setEditingAuditTemplate(template);
    setEditableAuditTemplate(initialEditable);
    setShowAuditReviewPanel(true);
    setIsLoadingAuditDetails(true);

    // Fetch full details asynchronously
    try {
      // Fetch full template details
      const result = await auditService.getTemplate(template.id);
      if (!result.success || !result.data) {
        showNotification('Error loading audit template details', 'error');
        setIsLoadingAuditDetails(false);
        return;
      }

      const fullTemplate = result.data;

      // Get compliance template names from IDs
      const complianceTemplateNames: string[] = [];
      if (fullTemplate.complianceTemplateIds && fullTemplate.complianceTemplateIds.length > 0) {
        // Fetch all compliance templates in parallel for better performance
        const compliancePromises = fullTemplate.complianceTemplateIds.map(async (templateId) => {
          const complianceResult = await complianceService.getTemplate(templateId);
          if (complianceResult.success && complianceResult.data) {
            return complianceResult.data.name;
          }
          return null;
        });

        const complianceNames = await Promise.all(compliancePromises);
        complianceTemplateNames.push(...complianceNames.filter((name): name is string => name !== null));
      }

      // Update with full details
      const editable: Partial<NewAuditTemplateSuggestion> = {
        name: fullTemplate.name,
        description: fullTemplate.description,
        auditType: fullTemplate.auditType,
        frequency: fullTemplate.frequency,
        complianceTemplateNames: complianceTemplateNames,
        missingComplianceTemplates: [], // Existing templates won't have missing ones
        why_relevant: '',
        metadata: {
          ...fullTemplate.metadata,
          customAuditType: fullTemplate.metadata?.customAuditTypeName || ''
        }
      };

      setEditingAuditTemplate(fullTemplate);
      setEditableAuditTemplate(editable);
    } catch (error: any) {
      console.error('Error loading audit template for edit:', error);
      showNotification(`Error loading details: ${error.message}`, 'error');
    } finally {
      setIsLoadingAuditDetails(false);
    }
  };

  const handleUpdateAuditTemplate = async () => {
    if (!currentOrganization?.id || !editableAuditTemplate || !editingAuditTemplate?.id) {
      return;
    }

    try {
      // Find compliance template IDs by matching names
      const complianceTemplateIds: string[] = [];
      if (editableAuditTemplate.complianceTemplateNames && editableAuditTemplate.complianceTemplateNames.length > 0) {
        editableAuditTemplate.complianceTemplateNames.forEach(name => {
          const matchingTemplate = templates.find(t => 
            t.name.toLowerCase().trim() === name.toLowerCase().trim()
          );
          if (matchingTemplate?.id) {
            complianceTemplateIds.push(matchingTemplate.id);
          }
        });
      }

      // Prepare update data
      const auditType = editableAuditTemplate.auditType || 'Custom';
      const metadata: any = {
        ...editingAuditTemplate.metadata,
        ...editableAuditTemplate.metadata
      };
      
      // If custom audit type with custom name, store it in metadata
      if (auditType === 'Custom' && editableAuditTemplate.metadata?.customAuditType) {
        metadata.customAuditTypeName = editableAuditTemplate.metadata.customAuditType;
      }

      const updateData: Partial<AuditTemplate> = {
        name: editableAuditTemplate.name || 'Untitled Audit',
        description: editableAuditTemplate.description,
        auditType: auditType,
        frequency: editableAuditTemplate.frequency || 'yearly',
        complianceTemplateIds: complianceTemplateIds,
        metadata: metadata
      };

      const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
      const result = await auditService.updateTemplate(editingAuditTemplate.id, updateData, userInfo);

      if (result.success) {
        await loadAuditTemplates();
        setShowAuditReviewPanel(false);
        setEditingAuditTemplate(null);
        setEditableAuditTemplate(null);
        showNotification('Audit template updated successfully', 'success');
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error updating audit template:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  const handleCancelAuditReview = () => {
    setShowAuditReviewPanel(false);
    setAuditTemplateToReview(null);
    setEditableAuditTemplate(null);
    setEditingAuditTemplate(null);
    setComplianceTemplateSearch('');
    setIsLoadingAuditDetails(false);
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
    const result = await complianceService.deleteTemplate(
      templateId,
      userInfo,
      currentOrganization?.id
    );
    if (result.success) {
      await loadTemplates();
      showNotification('Compliance deleted successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleCopy = async (templateId: string) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to copy compliance templates.', 'error');
      navigate('/subscription');
      return;
    }

    const result = await complianceService.copyTemplate(templateId);
    if (result.success) {
      await loadTemplates();
      showNotification('Compliance copied successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleAddClick = () => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add compliance templates.', 'error');
      navigate('/subscription');
      return;
    }

    if (!currentOrganization?.id) {
      setUpgradeModalMessage('You must have an organization to add compliance templates. Please create an organization first.');
      setShowUpgradeModal(true);
      return;
    }
    setIsAdding(true);
    setIsEditing(false);
    setSelectedTemplate(null);
    setTemplateToAdd(null);
  };

  const handleEdit = (template: ComplianceTemplate) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to edit compliance templates.', 'error');
      navigate('/subscription');
      return;
    }

    setSelectedTemplate(template);
    setIsEditing(true);
  };

  const handleAddTemplate = async (templateData: Partial<ComplianceTemplate>) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add compliance templates.', 'error');
      navigate('/subscription');
      setIsAdding(false);
      return;
    }

    if (!currentOrganization?.id) {
      setUpgradeModalMessage('You must have an organization to add compliance templates. Please create an organization first.');
      setShowUpgradeModal(true);
      setIsAdding(false);
      return;
    }

    const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
    const result = await complianceService.addTemplate(
      templateData as Omit<ComplianceTemplate, 'id'>, 
      currentOrganization?.id,
      userInfo
    );
    if (result.success) {
      await loadTemplates();
      setIsAdding(false);
      setSelectedTemplate(null);
      showNotification('Compliance added successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleUpdateTemplate = async (templateData: Partial<ComplianceTemplate>) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to edit compliance templates.', 'error');
      navigate('/subscription');
      setIsEditing(false);
      return;
    }

    if (!selectedTemplate?.id) {
      showNotification('Error: Template ID not found', 'error');
      return;
    }
    
    const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
    const result = await complianceService.updateTemplate(
      selectedTemplate.id, 
      templateData,
      userInfo,
      currentOrganization?.id
    );
    if (result.success) {
      await loadTemplates();
      setIsEditing(false);
      setSelectedTemplate(null);
      showNotification('Compliance updated successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleFormSubmit = async (templateData: Partial<ComplianceTemplate>) => {
    if (isEditing) {
      await handleUpdateTemplate(templateData);
    } else {
      await handleAddTemplate(templateData);
    }
    // Clear the template to add after successful submission
    setTemplateToAdd(null);
  };

  const handleAddFromSuggestion = async (suggestion: NewTemplateSuggestion) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add compliance templates.', 'error');
      navigate('/subscription');
      return;
    }

    if (!currentOrganization?.id) {
      setUpgradeModalMessage('You must have an organization to add compliance templates. Please create an organization first.');
      setShowUpgradeModal(true);
      return;
    }

    // Import calculateRiskWeight function
    const { calculateRiskWeight } = await import('@/firebase/complianceService');

    // Convert AI suggestion to template format
    const templateData: Partial<ComplianceTemplate> = {
      name: suggestion.name,
      category: suggestion.category,
      frequency: suggestion.frequency,
      owner_role: suggestion.owner_role,
      evidence_types: suggestion.evidence_types,
      pass_fail_criteria: suggestion.pass_fail_criteria,
      jurisdiction: suggestion.jurisdiction,
      jurisdiction_notes: suggestion.jurisdiction_notes,
      risk_level: suggestion.risk_level,
      risk_weight: calculateRiskWeight(suggestion.risk_level),
      lead_times_days: suggestion.lead_times_days,
      referenceDocuments: []
    };
    
    setTemplateToAdd(templateData);
    setIsAdding(true);
    setSelectedTemplate(null);
    setIsEditing(false);
    // Clear search to see the new template after creation
    setSearchTerm('');
  };

  const handleFormCancel = () => {
    setIsAdding(false);
    setIsEditing(false);
    setSelectedTemplate(null);
    setTemplateToAdd(null);
  };

  const handleLoadDefaultTemplates = async () => {
    if (!currentOrganization?.id) {
      showNotification('Please select an organization first', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const result = await loadDefaultTemplatesForOrganization(currentOrganization);
      
      if (result.success) {
        await loadTemplates();
        showNotification(
          `Successfully loaded ${result.count || 0} default templates for ${currentOrganization.name || 'your organization'}`,
          'success'
        );
      } else {
        showNotification(result.error || 'Failed to load default templates', 'error');
      }
    } catch (error: any) {
      console.error('Error loading default templates:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique categories from existing templates plus predefined categories from default templates
  const predefinedCategories = [
    'Life Safety',
    'Building & Facilities',
    'Licensing & Administrative',
    'Environmental & Utilities',
    'Insurance & Finance',
    'Training & HR'
  ];
  const existingCategories = Array.from(new Set(templates.map(t => t.category)));
  const categories = Array.from(new Set([...predefinedCategories, ...existingCategories]));

  const filteredTemplates = templates.filter(t => {
    // Filter by category
    const matchesCategory = selectedCategory === 'All Categories' || t.category === selectedCategory;
    
    // Filter by search term
    const matchesSearch = !searchTerm || 
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.owner_role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.jurisdiction.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="p-4 max-w-[95vw] mx-auto relative">
      {/* Backdrop overlay when panel is open */}
      {(isAdding || isEditing) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
          onClick={handleFormCancel}
        />
      )}
      
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-4">
        {/* Educational Banner for Free and Operate Plans */}
        {(() => {
          const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
          const isFreeOrOperatePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate' || normalizedPlan === 'operate';
          const isSubscriptionCancelled = subscription?.status === 'canceled';
          
          if (isFreeOrOperatePlan && !isSubscriptionCancelled) {
            return (
              <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Info className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Compliance Templates Available</h3>
                    <p className="text-sm text-blue-800 mb-4">
                      You can create and manage compliance templates on your current plan. However, <strong>tasks will not be automatically generated</strong> from these templates unless you upgrade to the <strong>Comply</strong> plan. Upgrade to enable automatic task generation from your compliance policies.
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Upgrade to Comply Plan
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('compliances')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'compliances'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Compliances
            </button>
            <button
              onClick={() => setActiveTab('audits')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'audits'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audits
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'compliances' && (
          <React.Fragment>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Compliance Templates</h2>
              <div className="flex gap-2">
                <button onClick={handleLoadDefaultTemplates} className="btn-secondary px-4 py-2">
                  Load Default Templates
                </button>
                <button onClick={handleAddClick} className="btn-primary px-4 py-2">
                  + Add Compliance
                </button>
                <button onClick={loadTemplates} className="btn-secondary px-4 py-2">
                  Refresh
                </button>
              </div>
            </div>

        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search templates... (AI-powered suggestions available)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md pr-10"
            />
            {isLoadingAI && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
                <span className="text-xs text-orange-600 font-medium animate-pulse">AI searching...</span>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading templates...</p>
          </div>
        ) : (
          <React.Fragment>
            {/* Categories */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Filter by Category</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('All Categories')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === 'All Categories'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Categories
                </button>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === cat
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Showing {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} 
                {selectedCategory !== 'All Categories' && ` in "${selectedCategory}"`}
              </p>
            </div>

            {/* Templates Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Compliance Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Tags</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Owner Role</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Evidences</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Pass/Fail Criteria</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Jurisdiction</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map((template, index) => (
                    <tr 
                      key={template.id} 
                      data-template-id={template.id}
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}
                    >
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="font-medium text-sm text-gray-900">{template.name}</div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex flex-wrap gap-1">
                          <span className={`px-2 py-1 rounded text-xs ${
                            template.risk_level === 'High' ? 'bg-red-100 text-red-800' :
                            template.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {template.risk_level}
                          </span>
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                            {template.frequency}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <span className="text-xs text-gray-700">{template.category}</span>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <span className="text-xs text-gray-700">{template.owner_role}</span>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {template.evidence_types.slice(0, 2).map((evidence, idx) => (
                            <span key={idx} className={`px-1.5 py-0.5 rounded text-xs ${
                              evidence.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {evidence.label} {evidence.required && '*'}
                            </span>
                          ))}
                          {template.evidence_types.length > 2 && (
                            <span className="text-xs text-gray-500">+{template.evidence_types.length - 2} more</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="text-xs text-gray-700 line-clamp-2" title={template.pass_fail_criteria}>
                          {template.pass_fail_criteria}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <span className="text-xs text-gray-700">{template.jurisdiction}</span>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => setTemplateForLocationAssignment(template)} 
                            className="text-indigo-600 hover:text-indigo-800 text-xs px-2 py-1"
                            title="Assign to Locations"
                          >
                            <MapPin className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleEdit(template)} 
                            className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => template.id && handleCopy(template.id)} 
                            className="text-gray-600 hover:text-gray-800 text-xs px-2 py-1"
                            title="Copy"
                          >
                            📋
                          </button>
                          <button 
                            onClick={() => template.id && handleDelete(template.id)} 
                            className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-8">
                {isLoadingAI ? (
                  <div className="border border-gray-200 rounded p-6">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Sparkles className="w-6 h-6 text-orange-500 animate-spin" />
                      <p className="text-lg font-medium text-gray-700">AI is finding suggestions...</p>
                    </div>
                    <p className="text-sm text-gray-500">Analyzing available templates for you</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* New Template Suggestions from External Knowledge - PRIORITY */}
                    {newTemplateSuggestions && newTemplateSuggestions.length > 0 && (
                      <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 text-left">
                        <div className="flex items-center gap-2 mb-4">
                          <Sparkles className="w-5 h-5 text-blue-600" />
                          <h3 className="text-lg font-semibold text-blue-900">New Template Suggestions</h3>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-4">
                          Based on compliance requirements, here are templates you might want to add to your system:
                        </p>
                        
                        <div className="space-y-4">
                          {newTemplateSuggestions.map((suggestion, index) => (
                            <div 
                              key={index}
                              className="bg-white rounded-lg p-5 border border-blue-200 hover:border-blue-300 hover:shadow-md transition-all"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <h5 className="font-semibold text-gray-900 text-lg mb-1">{suggestion.name}</h5>
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                      {suggestion.category}
                                    </span>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      suggestion.risk_level === 'High' ? 'bg-red-100 text-red-800' :
                                      suggestion.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                      {suggestion.risk_level} Risk
                                    </span>
                                    <span className="text-xs text-gray-600">
                                      {suggestion.frequency}
                                    </span>
                                  </div>
                                  {suggestion.description && (
                                    <p className="text-sm text-gray-700 mb-2">{suggestion.description}</p>
                                  )}
                                  <p className="text-xs text-gray-600 italic mb-3">"{suggestion.why_relevant}"</p>
                                  
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                      <div>
                                        <span className="font-medium text-gray-700">Owner:</span>
                                        <span className="ml-2 text-gray-600">{suggestion.owner_role}</span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Jurisdiction:</span>
                                        <span className="ml-2 text-gray-600">{suggestion.jurisdiction}</span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Evidence Types:</span>
                                        <span className="ml-2 text-gray-600">
                                          {suggestion.evidence_types.map(e => e.label).join(', ')}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Lead Times:</span>
                                        <span className="ml-2 text-gray-600">{suggestion.lead_times_days.join(', ')} days</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <button
                                onClick={() => handleAddFromSuggestion(suggestion)}
                                className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                              >
                                <Plus className="w-4 h-4" />
                                Add This Template
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Existing Template Suggestions or API Key Help - Only show if no new suggestions available */}
                    {!newTemplateSuggestions && aiSuggestion && (
                      <div className="border border-orange-200 rounded-lg p-6 bg-orange-50 text-left">
                        <div className="flex items-center gap-2 mb-4">
                          <Sparkles className="w-5 h-5 text-orange-600" />
                          <h3 className="text-lg font-semibold text-orange-900">
                            {aiSuggestion.suggestedTemplates && aiSuggestion.suggestedTemplates.length > 0 
                              ? 'Existing Templates in System'
                              : 'AI Search Configuration'}
                          </h3>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-4">{aiSuggestion.reason}</p>
                        
                        {aiSuggestion.suggestedTemplates && aiSuggestion.suggestedTemplates.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Suggested Templates:</h4>
                            <div className="space-y-3">
                              {aiSuggestion.suggestedTemplates.map((suggestion, index) => {
                                const template = templates.find(t => t.name === suggestion.name);
                                if (!template) return null;
                                
                                return (
                                  <div 
                                    key={index} 
                                    className="bg-white rounded-lg p-4 border border-orange-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer"
                                    onClick={() => {
                                      setSearchTerm(template.name);
                                      // Scroll to the template when it appears in results
                                      setTimeout(() => {
                                        document.querySelector(`[data-template-id="${template.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }, 100);
                                    }}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <h5 className="font-semibold text-gray-900 mb-1">{suggestion.name}</h5>
                                        <p className="text-xs text-gray-600 mb-2">{suggestion.category}</p>
                                        <p className="text-sm text-gray-700 italic">"{suggestion.why}"</p>
                                      </div>
                                      <span className="ml-3 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                                        Click to search
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {aiSuggestion.alternativeTerms && aiSuggestion.alternativeTerms.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">
                              {aiSuggestion.suggestedTemplates && aiSuggestion.suggestedTemplates.length > 0
                                ? 'Try these search terms:'
                                : 'Setup Instructions:'}
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {aiSuggestion.alternativeTerms.map((term, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    if (term.startsWith('Add ') || term.includes('.env')) {
                                      // Don't set search term for setup instructions
                                      return;
                                    }
                                    setSearchTerm(term);
                                  }}
                                  className={`px-3 py-1 rounded-full text-xs transition-colors ${
                                    term.startsWith('Add ') || term.includes('.env')
                                      ? 'bg-gray-100 text-gray-600 cursor-default'
                                      : 'bg-white border border-orange-300 text-gray-700 hover:bg-orange-100 hover:border-orange-400'
                                  }`}
                                >
                                  {term}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Fallback when no suggestions - show helpful message */}
                    {!aiSuggestion && !newTemplateSuggestions && (
                      <div className="text-center py-8 text-gray-500 border border-gray-200 rounded">
                        <div className="mb-4">
                          <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-lg font-medium">No templates found</p>
                          <p className="text-sm mt-1">
                            {searchTerm 
                              ? `No existing templates match "${searchTerm}". AI is searching for new compliance requirements...` 
                              : 'No templates match your filters.'}
                          </p>
                        </div>
                        {searchTerm && (
                          <div className="text-xs text-gray-400 mt-4 p-3 bg-gray-50 rounded">
                            <p className="font-medium mb-1">💡 AI Search Setup Required:</p>
                            <p>Add <code className="bg-gray-200 px-1 rounded">VITE_GOOGLE_AI_API_KEY</code> to your <code className="bg-gray-200 px-1 rounded">.env</code> file</p>
                            <p className="mt-1">to enable AI-powered template suggestions from external knowledge.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        )}
          </React.Fragment>
        )}

        {/* Audits Tab Content */}
        {activeTab === 'audits' && (
          <React.Fragment>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Audit Templates</h2>
              <div className="flex gap-2">
                <button 
                  onClick={loadAuditTemplates} 
                  className="btn-secondary px-4 py-2"
                  disabled={isLoadingAudits}
                >
                  Refresh
                </button>
                <button 
                  className="btn-primary px-4 py-2"
                  disabled={!currentOrganization?.id}
                >
                  + Create Audit Template
                </button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mb-4 space-y-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search audit templates... (AI-powered suggestions available)"
                  value={auditSearchTerm}
                  onChange={(e) => setAuditSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md pr-10"
                />
                {isLoadingAuditAI && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
                    <span className="text-xs text-orange-600 font-medium animate-pulse">AI searching...</span>
                  </div>
                )}
              </div>
              
              {/* Advanced Filters */}
              <div className="flex flex-wrap gap-2">
                <select
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                  onChange={(e) => {
                    // Filter by audit type
                    const type = e.target.value;
                    if (type === 'all') {
                      // Reset filter - handled by search term
                    }
                  }}
                  defaultValue="all"
                >
                  <option value="all">All Types</option>
                  <option value="SOC2">SOC2</option>
                  <option value="ISO27001">ISO 27001</option>
                  <option value="HIPAA">HIPAA</option>
                  <option value="PCI-DSS">PCI-DSS</option>
                  <option value="GDPR">GDPR</option>
                  <option value="Custom">Custom</option>
                </select>
                
                <select
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                  onChange={(e) => {
                    // Filter by frequency
                    const frequency = e.target.value;
                    if (frequency === 'all') {
                      // Reset filter
                    }
                  }}
                  defaultValue="all"
                >
                  <option value="all">All Frequencies</option>
                  <option value="yearly">Yearly</option>
                  <option value="semi-yearly">Semi-Yearly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
            </div>

            {/* Loading State */}
            {isLoadingAudits ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading audit templates...</p>
              </div>
            ) : (
              <>
                {/* Calculate filtered templates */}
                {(() => {
                  const filteredTemplates = auditTemplates.filter(template => {
                    if (!auditSearchTerm) return true;
                    const search = auditSearchTerm.toLowerCase();
                    return (
                      template.name.toLowerCase().includes(search) ||
                      template.auditType.toLowerCase().includes(search) ||
                      template.description?.toLowerCase().includes(search)
                    );
                  });

                  // Show table if we have filtered results
                  if (filteredTemplates.length > 0) {
                    return (
                      <div className="overflow-x-auto">
                        <table className="min-w-full table-auto border-collapse">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Audit Name</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Type</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Frequency</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Compliance Items</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Description</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTemplates.map((template, index) => (
                            <tr 
                              key={template.id} 
                              className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}
                            >
                              <td className="px-3 py-2 border border-gray-200">
                                <div className="font-medium text-sm text-gray-900">{template.name}</div>
                              </td>
                              <td className="px-3 py-2 border border-gray-200">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                  {template.auditType === 'Custom' && template.metadata?.customAuditTypeName
                                    ? template.metadata.customAuditTypeName
                                    : template.auditType}
                                </span>
                              </td>
                              <td className="px-3 py-2 border border-gray-200">
                                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                  {template.frequency === 'yearly' ? 'Yearly' :
                                   template.frequency === 'semi-yearly' ? 'Semi-Yearly' :
                                   'Quarterly'}
                                </span>
                              </td>
                              <td className="px-3 py-2 border border-gray-200">
                                <span className="text-xs text-gray-700">
                                  {template.complianceTemplateIds?.length || 0} item{template.complianceTemplateIds?.length !== 1 ? 's' : ''}
                                </span>
                              </td>
                              <td className="px-3 py-2 border border-gray-200">
                                <div className="text-xs text-gray-700 line-clamp-2" title={template.description}>
                                  {template.description || 'No description'}
                                </div>
                              </td>
                              <td className="px-3 py-2 border border-gray-200">
                                <div className="flex justify-center gap-1">
                                  <button 
                                    onClick={() => {
                                      setSelectedAuditTemplateForRun(template);
                                      setShowRunAuditModal(true);
                                    }}
                                    className="text-purple-600 hover:text-purple-800 text-xs px-2 py-1"
                                    title="Run Audit"
                                  >
                                    <Play className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => {
                                      setSelectedAuditTemplateForHistory(template);
                                      setShowAuditHistory(true);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-800 text-xs px-2 py-1"
                                    title="View History"
                                  >
                                    <History className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => handleEditAuditTemplate(template)}
                                    className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => template.id && handleDeleteAuditTemplate(template.id)}
                                    className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                    );
                  }

                  // Show AI suggestions or empty state if no filtered results
                  return (
                    <>
                      {/* AI Suggestions */}
                      {isLoadingAuditAI ? (
                      <div className="text-center py-8">
                        <div className="border border-gray-200 rounded p-6">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <Sparkles className="w-6 h-6 text-orange-500 animate-spin" />
                            <p className="text-lg font-medium text-gray-700">AI is finding audit suggestions...</p>
                          </div>
                          <p className="text-sm text-gray-500">Analyzing audit requirements for you</p>
                        </div>
                      </div>
                    ) : auditAiSuggestions && auditAiSuggestions.length > 0 ? (
                      <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 text-left">
                        <div className="flex items-center gap-2 mb-4">
                          <Sparkles className="w-5 h-5 text-blue-600" />
                          <h3 className="text-lg font-semibold text-blue-900">AI-Generated Audit Template Suggestions</h3>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-4">
                          Based on your search, here are audit templates you might want to add:
                        </p>
                        
                        <div className="space-y-4">
                          {auditAiSuggestions.map((suggestion, index) => (
                            <div 
                              key={index}
                              className="bg-white rounded-lg p-5 border border-blue-200 hover:border-blue-300 hover:shadow-md transition-all"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <h5 className="font-semibold text-gray-900 text-lg mb-1">{suggestion.name}</h5>
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                      {suggestion.auditType}
                                    </span>
                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                      {suggestion.frequency === 'yearly' ? 'Yearly' :
                                       suggestion.frequency === 'semi-yearly' ? 'Semi-Yearly' :
                                       'Quarterly'}
                                    </span>
                                  </div>
                                  {suggestion.description && (
                                    <p className="text-sm text-gray-700 mb-2">{suggestion.description}</p>
                                  )}
                                  <p className="text-xs text-gray-600 italic mb-3">"{suggestion.why_relevant}"</p>
                                  
                                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                                    {/* Existing Compliance Templates */}
                                    {suggestion.complianceTemplateNames && suggestion.complianceTemplateNames.length > 0 && (
                                      <div>
                                        <p className="text-xs font-medium text-gray-700 mb-2">✅ Suggested Compliance Templates (Existing):</p>
                                        <div className="flex flex-wrap gap-2">
                                          {suggestion.complianceTemplateNames.map((name, idx) => (
                                            <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                              {name}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Missing Compliance Templates */}
                                    {suggestion.missingComplianceTemplates && suggestion.missingComplianceTemplates.length > 0 && (
                                      <div>
                                        <p className="text-xs font-medium text-orange-700 mb-2">
                                          ⚠️ Missing Compliance Templates (Will be created):
                                        </p>
                                        <div className="space-y-2">
                                          {suggestion.missingComplianceTemplates.map((missing, idx) => (
                                            <div key={idx} className="bg-orange-50 border border-orange-200 rounded p-2">
                                              <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                  <p className="text-xs font-semibold text-gray-900">{missing.name}</p>
                                                  <p className="text-xs text-gray-600 mt-1">{missing.why_needed}</p>
                                                  <div className="flex flex-wrap gap-1 mt-1">
                                                    <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                                                      {missing.category}
                                                    </span>
                                                    <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                                                      {missing.frequency}
                                                    </span>
                                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                      missing.risk_level === 'High' ? 'bg-red-100 text-red-700' :
                                                      missing.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                      'bg-green-100 text-green-700'
                                                    }`}>
                                                      {missing.risk_level}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <button
                                onClick={() => handleAddAuditFromSuggestion(suggestion)}
                                className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                              >
                                <Plus className="w-4 h-4" />
                                Review & Create Audit Template
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-lg font-medium text-gray-700 mb-2">No audit templates found</p>
                        <p className="text-sm text-gray-500 mb-4">
                          {auditSearchTerm 
                            ? `No templates match "${auditSearchTerm}"`
                            : 'Get started by creating your first audit template'}
                        </p>
                        {auditSearchTerm && (
                          <div className="text-xs text-gray-400 mt-4 p-3 bg-gray-50 rounded max-w-md mx-auto">
                            <p className="font-medium mb-1">💡 AI Search Setup Required:</p>
                            <p>Add <code className="bg-gray-200 px-1 rounded">VITE_GOOGLE_AI_API_KEY</code> to your <code className="bg-gray-200 px-1 rounded">.env</code> file</p>
                            <p className="mt-1">to enable AI-powered audit template suggestions.</p>
                          </div>
                        )}
                        {!auditSearchTerm && (
                          <button 
                            className="btn-primary px-4 py-2"
                            disabled={!currentOrganization?.id}
                          >
                            + Create Audit Template
                          </button>
                        )}
                      </div>
                    )}
                    </>
                  );
                })()}
              </>
            )}

            {/* Run Audit Modal - Show only create form */}
            {showRunAuditModal && selectedAuditTemplateForRun && (
              <CreateAuditInstanceModal
                auditTemplate={selectedAuditTemplateForRun}
                onClose={() => {
                  setShowRunAuditModal(false);
                  setSelectedAuditTemplateForRun(null);
                }}
                onSuccess={() => {
                  setShowRunAuditModal(false);
                  setSelectedAuditTemplateForRun(null);
                  loadAuditTemplates();
                }}
              />
            )}

            {/* Audit History Modal */}
            {showAuditHistory && selectedAuditTemplateForHistory && (
              <AuditHistoryModal
                auditTemplate={selectedAuditTemplateForHistory}
                onClose={() => {
                  setShowAuditHistory(false);
                  setSelectedAuditTemplateForHistory(null);
                }}
              />
            )}
          </React.Fragment>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {(isAdding || isEditing) && (
        <ComplianceForm
          template={(templateToAdd && templateToAdd.name) ? templateToAdd as ComplianceTemplate : (selectedTemplate || undefined)}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          categories={categories}
        />
      )}
      {/* Floating Getting Started button on empty state */}
      {filteredTemplates.length === 0 && !isLoading && (
        <a
          href="/help/getting-started"
          className="fixed bottom-24 right-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-4 py-2 z-50"
          title="Getting Started"
        >
          Getting Started
        </a>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Organization Required</h3>
                <p className="text-sm text-gray-600">Action Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              {upgradeModalMessage}
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/organizations');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                Go to Organizations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Locations to Template Panel */}
      {templateForLocationAssignment && (
        <AssignLocationsToTemplatePanel
          template={templateForLocationAssignment}
          onClose={() => setTemplateForLocationAssignment(null)}
          onAssignComplete={() => {
            // Optionally reload templates or show a success message
            loadTemplates();
          }}
        />
      )}

      {/* Audit Template Review Panel */}
      {showAuditReviewPanel && editableAuditTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={handleCancelAuditReview}>
          <div 
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">
                  {editingAuditTemplate ? 'Edit Audit Template' : 'Review Audit Template'}
                </h2>
                <button
                  onClick={handleCancelAuditReview}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* Loading Indicator */}
              {isLoadingAuditDetails && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Loading audit details...</p>
                      <p className="text-xs text-blue-700">Fetching compliance templates and full information</p>
                    </div>
                  </div>
                </div>
              )}

              <div className={`space-y-6 ${isLoadingAuditDetails ? 'opacity-60 pointer-events-none' : ''}`}>
                {/* Audit Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audit Name *
                  </label>
                  <input
                    type="text"
                    value={editableAuditTemplate.name || ''}
                    onChange={(e) => setEditableAuditTemplate({ ...editableAuditTemplate, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter audit template name"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editableAuditTemplate.description || ''}
                    onChange={(e) => setEditableAuditTemplate({ ...editableAuditTemplate, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    rows={3}
                    placeholder="Enter audit description"
                  />
                </div>

                {/* Audit Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audit Type *
                  </label>
                  <select
                    value={editableAuditTemplate.auditType || 'Custom'}
                    onChange={(e) => setEditableAuditTemplate({ ...editableAuditTemplate, auditType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="SOC2">SOC2</option>
                    <option value="ISO27001">ISO 27001</option>
                    <option value="HIPAA">HIPAA</option>
                    <option value="PCI-DSS">PCI-DSS</option>
                    <option value="GDPR">GDPR</option>
                    <option value="NIST">NIST</option>
                    <option value="SOX">SOX (Sarbanes-Oxley)</option>
                    <option value="FISMA">FISMA</option>
                    <option value="FedRAMP">FedRAMP</option>
                    <option value="CMMC">CMMC</option>
                    <option value="Custom">Custom</option>
                  </select>
                  {editableAuditTemplate.auditType === 'Custom' && (
                    <input
                      type="text"
                      value={editableAuditTemplate.metadata?.customAuditType || ''}
                      onChange={(e) => setEditableAuditTemplate({ 
                        ...editableAuditTemplate, 
                        metadata: { 
                          ...editableAuditTemplate.metadata, 
                          customAuditType: e.target.value 
                        } 
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md mt-2"
                      placeholder="Enter custom audit type name"
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select the audit framework or standard this template follows
                  </p>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency *
                  </label>
                  <select
                    value={editableAuditTemplate.frequency || 'yearly'}
                    onChange={(e) => setEditableAuditTemplate({ ...editableAuditTemplate, frequency: e.target.value as 'yearly' | 'semi-yearly' | 'quarterly' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="yearly">Yearly</option>
                    <option value="semi-yearly">Semi-Yearly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>

                {/* Currently Attached Compliance Templates */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currently Attached Compliance Templates
                    {isLoadingAuditDetails ? (
                      <span className="ml-2 text-gray-400 font-normal text-xs">(Loading...)</span>
                    ) : editableAuditTemplate.complianceTemplateNames && editableAuditTemplate.complianceTemplateNames.length > 0 ? (
                      <span className="ml-2 text-gray-500 font-normal">
                        ({editableAuditTemplate.complianceTemplateNames.length})
                      </span>
                    ) : null}
                  </label>
                  
                  {isLoadingAuditDetails ? (
                    <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        <span>Loading compliance templates...</span>
                      </div>
                    </div>
                  ) : editableAuditTemplate.complianceTemplateNames && editableAuditTemplate.complianceTemplateNames.length > 0 ? (
                    <div className="border border-gray-200 rounded-md p-3 bg-gray-50 max-h-48 overflow-y-auto">
                      <div className="space-y-2">
                        {editableAuditTemplate.complianceTemplateNames.map((name, idx) => {
                          const matchingTemplate = templates.find(t => 
                            t.name.toLowerCase().trim() === name.toLowerCase().trim()
                          );
                          return (
                            <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                              <div className="flex-1">
                                <span className="text-sm text-gray-800 font-medium">{name}</span>
                                {matchingTemplate && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {matchingTemplate.category} • {matchingTemplate.owner_role}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = editableAuditTemplate.complianceTemplateNames?.filter((_, i) => i !== idx) || [];
                                  setEditableAuditTemplate({
                                    ...editableAuditTemplate,
                                    complianceTemplateNames: updated
                                  });
                                }}
                                className="ml-2 text-red-600 hover:text-red-800 text-xs px-2 py-1 hover:bg-red-50 rounded"
                                title="Remove"
                              >
                                ✕ Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 text-center">
                      <p className="text-sm text-gray-500">
                        No compliance templates attached yet. Add them from the available templates below.
                      </p>
                    </div>
                  )}
                </div>

                {/* Available Compliance Templates to Add */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Compliance Templates
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Search and add compliance templates to associate with this audit
                  </p>
                  
                  {/* Search Input */}
                  <div className="mb-3">
                    <input
                      type="text"
                      value={complianceTemplateSearch}
                      onChange={(e) => setComplianceTemplateSearch(e.target.value)}
                      placeholder="Search compliance templates..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  {/* Available Templates List */}
                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50 max-h-60 overflow-y-auto">
                    {(() => {
                      const availableTemplates = templates.filter(t => {
                        // Filter out already attached templates
                        const isAttached = editableAuditTemplate.complianceTemplateNames?.some(
                          name => name.toLowerCase().trim() === t.name.toLowerCase().trim()
                        );
                        if (isAttached) return false;
                        
                        // Filter by search term
                        if (!complianceTemplateSearch) return true;
                        const search = complianceTemplateSearch.toLowerCase();
                        return (
                          t.name.toLowerCase().includes(search) ||
                          t.category.toLowerCase().includes(search) ||
                          t.owner_role.toLowerCase().includes(search)
                        );
                      });

                      if (availableTemplates.length === 0) {
                        return (
                          <div className="text-center py-6">
                            <p className="text-sm text-gray-500">
                              {complianceTemplateSearch 
                                ? 'No matching templates found'
                                : 'All available templates are already attached'}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          {availableTemplates.map(template => {
                            const isAttached = editableAuditTemplate.complianceTemplateNames?.some(
                              name => name.toLowerCase().trim() === template.name.toLowerCase().trim()
                            );
                            
                            return (
                              <div key={template.id} className="flex items-center justify-between bg-white p-3 rounded border border-gray-200 hover:border-primary-300 transition-colors">
                                <div className="flex-1">
                                  <div className="font-medium text-sm text-gray-900">{template.name}</div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {template.category} • {template.owner_role}
                                  </div>
                                  {template.pass_fail_criteria && (
                                    <div className="text-xs text-gray-400 mt-1 line-clamp-1">
                                      {template.pass_fail_criteria}
                                    </div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentNames = editableAuditTemplate.complianceTemplateNames || [];
                                    if (!currentNames.some(name => name.toLowerCase().trim() === template.name.toLowerCase().trim())) {
                                      setEditableAuditTemplate({
                                        ...editableAuditTemplate,
                                        complianceTemplateNames: [...currentNames, template.name]
                                      });
                                    }
                                  }}
                                  disabled={isAttached}
                                  className="ml-3 px-3 py-1.5 text-xs font-medium text-primary-600 border border-primary-300 rounded hover:bg-primary-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                                  title={isAttached ? "Already attached" : "Add to audit"}
                                >
                                  <Plus className="w-3 h-3" />
                                  Add
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* AI-Suggested Compliance Templates (if any from AI) */}
                {auditTemplateToReview && auditTemplateToReview.complianceTemplateNames && auditTemplateToReview.complianceTemplateNames.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-green-600">✅</span> AI-Suggested Compliance Templates ({auditTemplateToReview.complianceTemplateNames.length})
                    </label>
                    <div className="border border-green-200 rounded-md p-4 bg-green-50 max-h-40 overflow-y-auto">
                      <div className="space-y-2">
                        {auditTemplateToReview.complianceTemplateNames.map((name, idx) => {
                          const matchingTemplate = templates.find(t => 
                            t.name.toLowerCase().trim() === name.toLowerCase().trim()
                          );
                          const isInSelected = editableAuditTemplate.complianceTemplateNames?.some(
                            n => n.toLowerCase().trim() === name.toLowerCase().trim()
                          );
                          return (
                            <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-green-200">
                              <div className="flex-1">
                                <span className="text-sm text-gray-800 font-medium">{name}</span>
                                {matchingTemplate && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {matchingTemplate.category} • {matchingTemplate.owner_role}
                                  </div>
                                )}
                              </div>
                              {isInSelected ? (
                                <span className="text-xs text-green-600 px-2 py-1 bg-green-100 rounded">✓ Added</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentNames = editableAuditTemplate.complianceTemplateNames || [];
                                    if (!currentNames.some(n => n.toLowerCase().trim() === name.toLowerCase().trim())) {
                                      setEditableAuditTemplate({
                                        ...editableAuditTemplate,
                                        complianceTemplateNames: [...currentNames, name]
                                      });
                                    }
                                  }}
                                  className="text-xs text-green-600 hover:text-green-800 px-2 py-1 border border-green-300 rounded hover:bg-green-50"
                                  title="Add to audit"
                                >
                                  + Add
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      These were suggested by AI. Click "+ Add" to include them in the audit.
                    </p>
                  </div>
                )}

                {/* Missing Compliance Templates */}
                {editableAuditTemplate.missingComplianceTemplates && editableAuditTemplate.missingComplianceTemplates.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-orange-700 mb-2">
                      Missing Compliance Templates ({editableAuditTemplate.missingComplianceTemplates.length}) - Will be created
                    </label>
                    <div className="border border-orange-200 rounded-md p-4 bg-orange-50 max-h-60 overflow-y-auto">
                      <div className="space-y-3">
                        {editableAuditTemplate.missingComplianceTemplates.map((missing, idx) => (
                          <div key={idx} className="bg-white p-3 rounded border border-orange-200">
                            <div className="flex items-start justify-between mb-2">
                              <input
                                type="text"
                                value={missing.name}
                                onChange={(e) => {
                                  const updated = [...(editableAuditTemplate.missingComplianceTemplates || [])];
                                  updated[idx] = { ...updated[idx], name: e.target.value };
                                  setEditableAuditTemplate({ ...editableAuditTemplate, missingComplianceTemplates: updated });
                                }}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-medium"
                                placeholder="Compliance template name"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                              <div>
                                <span className="font-medium">Category:</span> {missing.category}
                              </div>
                              <div>
                                <span className="font-medium">Frequency:</span> {missing.frequency}
                              </div>
                              <div>
                                <span className="font-medium">Risk:</span> {missing.risk_level}
                              </div>
                              <div>
                                <span className="font-medium">Owner:</span> {missing.owner_role}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2 italic">"{missing.why_needed}"</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-orange-600 mt-2">
                      These compliance templates will be created automatically when you create the audit template
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={handleCancelAuditReview}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAuditTemplate}
                    disabled={!editableAuditTemplate.name || !editableAuditTemplate.auditType}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {editingAuditTemplate ? 'Update Audit Template' : 'Create Audit Template'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceManagement;

